import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { RefreshCw, Car, Flag, ParkingCircle, ArrowDownToDot, Filter } from "lucide-react";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtRel = (iso) => {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return `${Math.max(1, Math.floor(d))}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  return `${Math.floor(d / 3600)}h ago`;
};

const STATUS_COLOR = {
  entered: "hsl(var(--primary))",
  parked:  "hsl(var(--success))",
  exited:  "hsl(var(--muted-foreground))",
};

const Tile = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div className="panel p-3 flex flex-col gap-0.5 min-w-0" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="micro-label">{label}</span>
      <Icon className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
    </div>
    <div className={`font-mono text-2xl font-semibold tracking-tighter leading-none mt-1 ${accent || ""}`}>{value}</div>
    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 truncate">{sub}</div>
  </div>
);

export default function Parking() {
  const [snap, setSnap] = useState(null);
  const [rows, setRows] = useState([]);
  const [filterLot, setFilterLot] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterFlagged, setFilterFlagged] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [s, v] = await Promise.all([
        http.get("/parking/snapshot").then((r) => r.data),
        http.get("/parking/vehicles?limit=60").then((r) => r.data),
      ]);
      setSnap(s);
      setRows(v.vehicles ?? []);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 7000);
    return () => clearInterval(t);
  }, []);

  const filtered = rows.filter((v) => {
    if (filterLot !== "all" && v.lot !== filterLot) return false;
    if (filterStatus !== "all" && v.status !== filterStatus) return false;
    if (filterFlagged && !v.flagged) return false;
    return true;
  });

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="h-10 flex items-center justify-between px-4 border-b border-border bg-[hsl(var(--surface))] shrink-0">
        <div className="flex items-baseline gap-3">
          <h1 className="font-display font-black uppercase tracking-tight text-sm leading-none">Parking & ANPR</h1>
          <span className="micro-label hidden sm:inline">Lot Occupancy · Vehicle Log · Watchlist</span>
        </div>
        <button onClick={load} className="btn-tac !px-2 !py-1.5" data-testid="parking-refresh-btn" aria-label="Refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-0">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Tile icon={Car}            label="Vehicles On-Site" value={fmt(snap?.total_vehicles)}  sub="last 80 ANPR scans"      testid="parking-kpi-total" />
          <Tile icon={ParkingCircle}  label="North Lot Fill"   value={`${Math.round((snap?.lots?.[0]?.pct ?? 0) * 100)}%`} sub={`${fmt(snap?.lots?.[0]?.occupancy)} / ${fmt(snap?.lots?.[0]?.capacity)}`} accent={(snap?.lots?.[0]?.pct ?? 0) > 0.85 ? "sev-critical" : ""} testid="parking-kpi-north" />
          <Tile icon={ParkingCircle}  label="South Lot Fill"   value={`${Math.round((snap?.lots?.[1]?.pct ?? 0) * 100)}%`} sub={`${fmt(snap?.lots?.[1]?.occupancy)} / ${fmt(snap?.lots?.[1]?.capacity)}`} accent={(snap?.lots?.[1]?.pct ?? 0) > 0.85 ? "sev-critical" : ""} testid="parking-kpi-south" />
          <Tile icon={Flag}           label="Flagged ANPR"     value={fmt(snap?.flagged_vehicles)} sub="watchlist hits"          accent={(snap?.flagged_vehicles ?? 0) > 0 ? "sev-critical" : ""} testid="parking-kpi-flagged" />
        </div>

        {/* Lots + Type breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
          <div className="panel p-3 lg:col-span-2">
            <div className="h-widget mb-2">Lots</div>
            <div className="space-y-3">
              {(snap?.lots ?? []).map((lot) => {
                const pct = Math.round(lot.pct * 100);
                const color = pct >= 85 ? "hsl(var(--destructive))" : pct >= 70 ? "hsl(var(--warning))" : "hsl(var(--primary))";
                return (
                  <div key={lot.zone_id} data-testid={`parking-lot-${lot.zone_id}`}>
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="font-display font-bold uppercase tracking-tight text-xs">{lot.name}</span>
                      <span className="font-mono text-xs"><span style={{ color }}>{pct}%</span> <span className="text-muted-foreground">· {fmt(lot.occupancy)} / {fmt(lot.capacity)}</span></span>
                    </div>
                    <div className="h-2 bg-border w-full">
                      <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="panel p-3">
            <div className="h-widget mb-2">By Vehicle Type</div>
            <div className="space-y-1.5">
              {(snap?.by_type ?? []).map((t) => {
                const total = snap?.total_vehicles || 1;
                const pct = Math.round((t.count / total) * 100);
                return (
                  <div key={t.type} className="flex items-center gap-2">
                    <span className="font-display font-bold uppercase tracking-tight text-[11px] w-20 shrink-0">{t.type}</span>
                    <div className="flex-1 h-1.5 bg-border"><div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${pct}%` }} /></div>
                    <span className="font-mono text-xs w-6 text-right">{t.count}</span>
                  </div>
                );
              })}
              {!snap?.by_type?.length && <div className="text-xs text-muted-foreground">Loading…</div>}
            </div>
          </div>
        </div>

        {/* Recent arrivals strip */}
        {snap?.last_arrivals?.length > 0 && (
          <div className="panel p-3">
            <div className="h-widget mb-2 flex items-center gap-1"><ArrowDownToDot className="w-3.5 h-3.5" /> Latest Arrivals</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {snap.last_arrivals.slice(0, 5).map((v) => (
                <div key={v.id} className="panel-elevated p-2" data-testid={`parking-arrival-${v.id}`}>
                  <div className="font-mono font-semibold text-sm tracking-tighter">{v.plate}</div>
                  <div className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest mt-0.5">
                    {v.type} · {v.lot_name}
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground mt-0.5">{fmtRel(v.ts)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vehicle log table with filters */}
        <div className="panel">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-border flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="h-widget">Vehicle Log</span>
              <span className="font-mono text-[10px] text-muted-foreground">{filtered.length} / {rows.length}</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <Filter className="w-3 h-3 text-muted-foreground" />
              {["all", "parking-north", "parking-south"].map((l) => (
                <button key={l} onClick={() => setFilterLot(l)} data-testid={`parking-filter-lot-${l}`}
                  className={["px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-bold border transition-colors",
                    filterLot === l ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "border-border text-muted-foreground hover:text-foreground"].join(" ")}>
                  {l === "all" ? "All" : l === "parking-north" ? "North" : "South"}
                </button>
              ))}
              <span className="w-2" />
              {["all", "entered", "parked", "exited"].map((s) => (
                <button key={s} onClick={() => setFilterStatus(s)} data-testid={`parking-filter-status-${s}`}
                  className={["px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-bold border transition-colors",
                    filterStatus === s ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]" : "border-border text-muted-foreground hover:text-foreground"].join(" ")}>
                  {s}
                </button>
              ))}
              <button onClick={() => setFilterFlagged(!filterFlagged)} data-testid="parking-filter-flagged"
                className={["px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-bold border transition-colors flex items-center gap-1",
                  filterFlagged ? "border-[hsl(var(--destructive))] text-[hsl(var(--destructive))]" : "border-border text-muted-foreground hover:text-foreground"].join(" ")}>
                <Flag className="w-3 h-3" /> Flagged
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-[hsl(var(--surface-elevated))]">
                  <th className="text-left px-3 py-2 micro-label">Plate</th>
                  <th className="text-left px-3 py-2 micro-label">Type</th>
                  <th className="text-left px-3 py-2 micro-label">Lot</th>
                  <th className="text-left px-3 py-2 micro-label">Status</th>
                  <th className="text-left px-3 py-2 micro-label">Time</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => (
                  <tr key={v.id} className="border-b border-border hover:bg-[hsl(var(--surface-elevated))]" data-testid={`parking-row-${v.id}`}>
                    <td className="px-3 py-2 font-mono font-semibold tracking-tighter">
                      {v.plate}
                      {v.flagged && <span className="ml-2 inline-flex items-center gap-1 px-1 py-0.5 text-[9px] uppercase tracking-widest font-bold border border-[hsl(var(--destructive))] text-[hsl(var(--destructive))]"><Flag className="w-2.5 h-2.5" /> {v.flag_reason}</span>}
                    </td>
                    <td className="px-3 py-2 font-mono">{v.type}</td>
                    <td className="px-3 py-2 font-mono text-muted-foreground">{v.lot_name}</td>
                    <td className="px-3 py-2"><span className="font-mono text-[10px] uppercase tracking-widest font-bold" style={{ color: STATUS_COLOR[v.status] }}>{v.status}</span></td>
                    <td className="px-3 py-2 font-mono text-muted-foreground">{fmtRel(v.ts)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-muted-foreground">No vehicles match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
